import { readLocale, translate } from '~/core/i18n'

/**
 * These labels are assembled by plain functions, not components, so they read the
 * locale rather than the context. A locale change rewrites the cookie and re-renders
 * the tree, so the next call already answers in the new language.
 */
function say(key: string, vars?: Record<string, string | number>): string {
  return translate(readLocale(), key, vars)
}

/**
 * Names a multi-row selection for a confirmation prompt: one row reads as itself, several read
 * as a count. Shared so the wording stays identical across every resource list — the phrasing
 * appears in destructive confirms, where an inconsistency reads as a different action.
 */
export function selectionLabel(count: number, firstName: string | undefined): string {
  if (count === 1) return firstName ?? say('selected item')
  return say('{count} selected items', { count })
}

export function selectionActionLabel(
  action: string,
  selectedCount: number,
  singleItemLabel = action
): string {
  if (selectedCount <= 1) return say(singleItemLabel)
  return countedSelectionActionLabel(action, selectedCount)
}

function countedSelectionActionLabel(action: string, count: number): string {
  return say(count === 1 ? '{action} {count} item' : '{action} {count} items', {
    action: say(action),
    count,
  })
}

interface SelectionToggleActionLabelOptions {
  selectedCount: number
  enabledCount: number
  disabledCount: number
  isSelectedItemEnabled: boolean
  hasExactAffectedCount?: boolean
}

export function selectionToggleActionLabel({
  selectedCount,
  enabledCount,
  disabledCount,
  isSelectedItemEnabled,
  hasExactAffectedCount = true,
}: SelectionToggleActionLabelOptions): string {
  if (selectedCount <= 1) return isSelectedItemEnabled ? say('Disable') : say('Enable')
  const action = disabledCount > 0 ? 'Enable' : 'Disable'
  if (!hasExactAffectedCount) return say('{action} selected items', { action: say(action) })
  return countedSelectionActionLabel(action, disabledCount > 0 ? disabledCount : enabledCount)
}
